import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import { useStacklineTheme } from '@stackline/ui';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';

import { Link } from 'react-router-dom';
import { useAppSelector } from 'src/utils/Hooks';
import { BusinessUnitsCreationModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/BusinessUnitsCreation/BusinessUnitsCreationModal';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import TimePeriodDropdown, {
  getAtlasBeaconTimePeriods
} from 'src/components/BeaconV2Components/common/TimePeriodDropdown';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import SegmentsViewTab from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsViewTab';
import AtlasV2BusinessUnitsTab from 'src/components/AtlasV2Components/AtlasV2LayoutComponents/AtlasV2HomeComponents/AtlasV2BusinessUnitsTab';
import { useFetchAndPollingSegments } from 'src/components/AtlasV2Components/Hooks/useFetchProcessingSegments';

enum SegmentsTab {
  mySegments = 'mySegments',
  teamSegments = 'teamSegments',
  businessUnits = 'businessUnits'
}

interface SegmentsNavigationTabsProps {
  tab: SegmentsTab;
  setTab: (tab: SegmentsTab) => void;
}

/**
 * List of tabs for the top of the segments page
 */
const SegmentsNavigationTabs = ({ tab, setTab }: SegmentsNavigationTabsProps) => {
  return (
    <GenericNavigationTabs
      tab={tab}
      tabs={[
        {
          label: 'My Segments',
          onClick: () => setTab(SegmentsTab.mySegments),
          value: SegmentsTab.mySegments
        },
        {
          label: 'Team Segments',
          onClick: () => setTab(SegmentsTab.teamSegments),
          value: SegmentsTab.teamSegments
        },
        {
          label: 'Business Units',
          onClick: () => setTab(SegmentsTab.businessUnits),
          value: SegmentsTab.businessUnits
        }
      ]}
    />
  );
};

/**
 * Segments page container
 */
const AtlasV2SegmentsPage = () => {
  const theme = useStacklineTheme();
  const [tab, setTab] = useState(SegmentsTab.mySegments);
  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  const { mySegments, teamSegments } = useAppSelector((state) => state.segments);
  const { processingSegments } = useFetchAndPollingSegments();
  const [businessUnitCreationModalOpen, setBusinessUnitCreationModalOpen] = useState(false);

  const handleClose = () => {
    setBusinessUnitCreationModalOpen(false);
  };

  return (
    <BasePageContainer sx={{ width: '100%', marginTop: '0px' }}>
      <Box display="flex" gap="44px" width="100%">
        <Box width="100%">
          <Box display="flex" justifyContent="space-between" width="100%">
            <Text variant="h2">Segments</Text>
            {[SegmentsTab.mySegments, SegmentsTab.teamSegments].includes(tab) && (
              <Link to={`/search${searchParams}&entityType=segment&action=create`}>
                <SlButton variant="contained">+ Create Segment</SlButton>
              </Link>
            )}
            {tab === SegmentsTab.businessUnits && (
              <Flex gap={16}>
                <TimePeriodDropdown allowedTimePeriods={getAtlasBeaconTimePeriods} />
                <SlButton
                  disabled={mySegments.length === 0 && teamSegments.length === 0} // Disable BU creation if no segments exist
                  onClick={() => setBusinessUnitCreationModalOpen(true)}
                  variant="contained"
                >
                  + Create Business Unit
                </SlButton>
              </Flex>
            )}
          </Box>
          <Box marginBottom={theme.spacing.lg}>
            <SegmentsNavigationTabs tab={tab} setTab={setTab} />
          </Box>
          <Box width="100%">
            {tab === SegmentsTab.mySegments && <SegmentsViewTab processingSegments={processingSegments} />}
            {tab === SegmentsTab.teamSegments && <SegmentsViewTab team processingSegments={processingSegments} />}
            {tab === SegmentsTab.businessUnits && <AtlasV2BusinessUnitsTab />}
            {businessUnitCreationModalOpen && (
              <BusinessUnitsCreationModal handleClose={handleClose} open={businessUnitCreationModalOpen} />
            )}
          </Box>
        </Box>
      </Box>
    </BasePageContainer>
  );
};

export default AtlasV2SegmentsPage;
